/**
 * Responsibility: decide where trees stand.
 *
 * ## Why this is shared rather than living in the forest
 *
 * Two biomes grow woodland — plain forest, and the wooded lake country — and a third could. One generator keeps a single
 * authoritative placement rule instead of copies that drift, exactly as `rockColumns.js` does for three rock biomes.
 * Extracted from `biomes/forest.js` unchanged, with only the noise labels parameterised, so existing forests are
 * byte-identical.
 *
 * ## Patchy, not a canopy
 *
 * This is the whole design constraint, and it comes from the steering file's rule against anything large and uniform. An
 * unbroken sea of treetops is as visually dead as a lake.
 *
 * Two levels produce the patchiness. Trees occupy slots on a fine grid anchored to absolute world coordinates, and a
 * separate medium-frequency **stand field** decides how much of that grid gets used. Below its threshold nothing grows —
 * those are the clearings. Above it, most slots fill in. The result is stands with open ground between them, alternating
 * every few seconds of flight.
 *
 * A single flat density would sprinkle trees evenly everywhere, which reads as texture rather than woodland and has no gaps
 * to make it interesting.
 *
 * ## One tile owns each tree
 *
 * A slot belongs to whichever tile contains its centre, the same rule the city uses for blocks, and for the same reason:
 * tile size is not a multiple of slot spacing, so slots straddle tile seams constantly and without a single owner every
 * straddling tree would be emitted twice.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from './noise.js';
import { createLocalRng, deriveSeed } from './rng.js';
import { FOREST } from '../config.js';

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

function clamp01(value) {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * Range of slot indices whose centres fall inside a span.
 *
 * Identical reasoning to the city's block range: the centre of slot `i` is `(i + 0.5) * spacing`, and requiring that to
 * land inside the span gives these bounds. Off by one here either doubles a row of trees along every tile seam or leaves a
 * bare stripe.
 */
function slotRange(start, length, spacing) {
  return {
    first: Math.ceil(start / spacing - 0.5),
    last: Math.ceil((start + length) / spacing - 0.5) - 1,
  };
}

/**
 * Build a tree placement function for a biome.
 *
 * @param {number} seed - Base world seed.
 * @param {string} biomeId - Labels the streams, so two wooded biomes do not grow identical stands where they meet.
 * @returns {(region: object, context: object) => Array<object>}
 */
export function createTreeStands(seed, biomeId) {
  /*
    Independent of the biome's own suitability field and of its ground, so where trees stand is not correlated with where
    the hills happen to rise.
  */
  const treeSeed = deriveSeed(seed, `structures:${biomeId}`);

  const standField = createFbm(seed, `stand:${biomeId}`, {
    octaves: FOREST.STAND_OCTAVES,
    baseFrequency: 1 / FOREST.STAND_WAVELENGTH,
  });

  /** Widest a crown may ever be, regardless of how height and spacing are tuned. */
  const maxCrownRadius = FOREST.TREE_SPACING * FOREST.CROWN_MAX_FRACTION;

  /*
    Second tier radius as a fraction of the crown radius, derived so the ring sits exactly on the cone the crown spokes
    describe rather than at an arbitrary width.

    The crown tapers linearly from full radius at TRUNK_RATIO of the height to nothing at the tip, so the radius at
    TIER_HEIGHT follows from where that is along the taper. A hand-picked fraction poked outside the cone, which left the
    ring floating clear of the surface and, once trees gained a depth mask, sticking out through it.
  */
  const tierRadiusRatio = 1 - (FOREST.TIER_HEIGHT - FOREST.TRUNK_RATIO) / (1 - FOREST.TRUNK_RATIO);

  /**
   * How strongly a stand covers this point, 0 to 1.
   *
   * Zero below the threshold — that is what makes clearings genuinely empty rather than merely thinner, which is the
   * difference between patchy and uniform.
   */
  function standStrength(x, z) {
    const raw = toUnitRange(standField(x, z));
    if (raw <= FOREST.STAND_THRESHOLD) return 0;

    /*
      Ramped over a narrow band rather than the whole remaining range. fBm concentrates around the middle of its range and
      almost never reaches 1, so dividing by `1 - threshold` left stand strength near 0.1 everywhere and the forest came
      out as scattered singles instead of stands. See STAND_RAMP.
    */
    const above = clamp01((raw - FOREST.STAND_THRESHOLD) / FOREST.STAND_RAMP);
    return Math.pow(above, FOREST.STAND_SHARPNESS);
  }

  /**
   * @param {{originX: number, originZ: number, size: number}} region - Tile bounds.
   * @param {object} context
   * @param {(x: number, z: number) => number} context.sampleWeight - This biome's weight.
   * @param {(x: number, z: number) => {elevation: number, slope: number}} context.sampleGroundDetail - Height *and*
   *   steepness of the drawn surface, from one set of samples. Must be the drawn surface, or trees float above or sink into
   *   the visible wireframe.
   * @param {(x: number, z: number, radius: number) => boolean} [context.isReserved]
   * @returns {Array<object>} Tree descriptions.
   */
  return function collectStructures(region, { sampleWeight, sampleGroundDetail, isReserved }) {
    const spacing = FOREST.TREE_SPACING;
    const xRange = slotRange(region.originX, region.size, spacing);
    const zRange = slotRange(region.originZ, region.size, spacing);

    const trees = [];
    const group = FOREST.WEIGHT_SAMPLE_SLOTS;

    /*
      Slots are walked in groups so the biome weight can be sampled once per group. The biome field varies over 14,000
      units, so one sample per few hundred is still far finer than anything it could resolve, and it removes the great
      majority of the cost.
    */
    for (let groupX = Math.floor(xRange.first / group); groupX <= Math.floor(xRange.last / group); groupX += 1) {
      for (let groupZ = Math.floor(zRange.first / group); groupZ <= Math.floor(zRange.last / group); groupZ += 1) {
        const groupCenterX = (groupX * group + group / 2) * spacing;
        const groupCenterZ = (groupZ * group + group / 2) * spacing;

        const weight = sampleWeight(groupCenterX, groupCenterZ);
        if (weight <= 0) continue;

        const weightDensity = FOREST.BASE_DENSITY * Math.pow(weight, FOREST.DENSITY_FALLOFF);
        if (weightDensity <= 0) continue;

        for (let slotX = groupX * group; slotX < (groupX + 1) * group; slotX += 1) {
          if (slotX < xRange.first || slotX > xRange.last) continue;

          for (let slotZ = groupZ * group; slotZ < (groupZ + 1) * group; slotZ += 1) {
            if (slotZ < zRange.first || slotZ > zRange.last) continue;

            const slotCenterX = (slotX + 0.5) * spacing;
            const slotCenterZ = (slotZ + 0.5) * spacing;

            const stand = standStrength(slotCenterX, slotCenterZ);
            if (stand <= 0) continue;

            /*
              One stream per slot, keyed by slot index rather than by tile, so the layout does not depend on which tile is
              being built or the order tiles are visited in.
            */
            const random = createLocalRng(treeSeed, slotX, slotZ);

            /*
              Roll order is part of the world's identity. Reordering these lines, or inserting a roll before an existing
              one, changes every forest at every seed. Append only.
            */
            const presenceRoll = random();
            const heightRoll = random();
            const jitterRollX = random();
            const jitterRollZ = random();
            const accentRoll = random();

            if (presenceRoll >= weightDensity * stand) continue;

            const shaped = Math.pow(heightRoll, FOREST.HEIGHT_SKEW);
            const height = lerp(FOREST.MIN_HEIGHT, FOREST.MAX_HEIGHT, shaped);
            const crownRadius = Math.min(height * FOREST.CROWN_RADIUS_RATIO, maxCrownRadius);

            /*
              Jitter limited to the slack left after reserving MIN_TREE_GAP between the widest possible crowns. Same
              guarantee as building jitter against street width: two neighbouring trees can never touch, so they stay
              individually distinct.
            */
            const slack = Math.max(0, (spacing - 2 * maxCrownRadius - FOREST.MIN_TREE_GAP) / 2);
            const x = slotCenterX + (jitterRollX * 2 - 1) * slack;
            const z = slotCenterZ + (jitterRollZ * 2 - 1) * slack;

            /*
              Keep out of another biome's structures — and, where a biome composes this with water of its own, out of that
              too. At a city border both biomes have weight and both build, so without this trees grow up through towers.

              Checked after every roll is consumed and before the ground is sampled: rejecting here cannot shift the random
              sequence for any other slot, and it saves the terrain samples for a tree that is not going to exist. Passing
              the crown radius rather than a point is what keeps branches out of walls as well as trunks out of floors.
            */
            if (isReserved?.(x, z, crownRadius)) continue;

            /*
              Height and slope from one sample. Probing for slope separately meant three surface samples per tree — twelve
              blended elevation evaluations where four do — and that was most of the cost of building a forest tile.
            */
            const { elevation: groundY, slope } = sampleGroundDetail(x, z);
            if (slope > FOREST.MAX_GROUND_SLOPE) continue;

            const hasSecondTier = height >= FOREST.TIER_MIN_HEIGHT;

            trees.push({
              kind: 'tree',
              x,
              z,
              groundY,
              height,
              crownRadius,
              trunkHeight: height * FOREST.TRUNK_RATIO,
              secondTierY: hasSecondTier ? groundY + height * FOREST.TIER_HEIGHT : null,
              secondTierRadius: hasSecondTier ? crownRadius * tierRadiusRatio : 0,
              accent: accentRoll < FOREST.ACCENT_CHANCE,
              weight,
              stand,
            });
          }
        }
      }
    }

    return trees;
  };
}
